import { useEffect, useState, useMemo, useRef, Suspense } from "react";
import axios from "axios";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { Box, LineChart, Layers3, Loader2, RotateCw, Maximize2, Minimize2 } from "lucide-react";
import TimeSlider, { useTradeTimeline } from "@/components/TradeTimeSlider";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SECTOR_ORDER = [
  "Technology", "Financials", "Healthcare", "Consumer", "Energy",
  "Industrials", "Utilities", "Materials", "Real Estate", "Bonds/Safety",
  "Inverse ETF", "Crypto", "Other",
];

const VIEWS = [
  { id: "scatter", label: "Trade Scatter", icon: LineChart, subtitle: "Time × P&L × Sector" },
  { id: "towers", label: "Trader Towers", icon: Box, subtitle: "P&L height per trader" },
  { id: "sectors", label: "Sector Cubes", icon: Layers3, subtitle: "Total P&L per sector" },
];

// ─── Data hook ────────────────────────────────────────────────────────────────

function useVizData(scope) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastLoaded, setLastLoaded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/visualization/trades?limit=400&scope=${scope}`, { withCredentials: true });
      setData(r.data);
      setLastLoaded(new Date());
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, lastLoaded, reload: load };
}

function niceMoney(n) {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

// ─── Imperative Three.js scene builder (avoids R3F JSX + Babel plugin conflict) ─
//
// The Emergent visual-edits Babel plugin injects x-line-number/x-column/etc onto
// every JSX element. R3F's reconciler treats those unknown kebab-case props as
// Three.js attach paths (e.g. x.line.number = ...) and throws at mount. So we
// build the entire 3D scene imperatively with plain THREE calls — nothing
// touches React reconciliation.

function makeSprite(text, color = "#eab308") {
  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  const fontPx = 32;
  canvas.width = 512 * dpr;
  canvas.height = 96 * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  ctx.font = `500 ${fontPx}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.shadowColor = "rgba(0,0,0,0.9)";
  ctx.shadowBlur = 6;
  ctx.fillStyle = color;
  ctx.fillText(text, 256, 48);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(4, 0.75, 1);
  sprite.renderOrder = 999;
  return sprite;
}

function buildScatter(items, myTrader, highlightMine) {
  const group = new THREE.Group();
  if (!items || items.length === 0) return group;

  const sortedByTime = [...items].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const t0 = new Date(sortedByTime[0]?.timestamp || Date.now()).getTime();
  const tN = new Date(sortedByTime[sortedByTime.length - 1]?.timestamp || Date.now()).getTime();
  const tRange = Math.max(tN - t0, 1);
  const maxAbsPnl = Math.max(1, ...items.map(t => Math.abs(t.pnl || 0)));
  const usedSectors = Array.from(new Set(items.map(t => t.sector)));
  usedSectors.sort((a, b) => {
    const ai = SECTOR_ORDER.indexOf(a); const bi = SECTOR_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const sectorSpacing = 3;
  const sectorCenter = (usedSectors.length - 1) / 2;
  const xMax = 12;

  // Y axis (P&L)
  const yAxis = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 16, 0.06),
    new THREE.MeshStandardMaterial({ color: 0xeab308, emissive: 0xeab308, emissiveIntensity: 0.3 })
  );
  group.add(yAxis);

  // X axis (time)
  const xAxis = new THREE.Mesh(
    new THREE.BoxGeometry(xMax * 2, 0.04, 0.04),
    new THREE.MeshStandardMaterial({ color: 0x666666 })
  );
  group.add(xAxis);

  // Axis labels
  const plusPnl = makeSprite("+P&L", "#22c55e"); plusPnl.position.set(0, 8.8, 0); group.add(plusPnl);
  const minusPnl = makeSprite("-P&L", "#ef4444"); minusPnl.position.set(0, -8.8, 0); group.add(minusPnl);
  const older = makeSprite("← older", "#888"); older.position.set(-xMax - 1.2, 0.5, 0); older.scale.set(3, 0.6, 1); group.add(older);
  const newer = makeSprite("newer →", "#888"); newer.position.set(xMax + 1.2, 0.5, 0); newer.scale.set(3, 0.6, 1); group.add(newer);

  // Sector labels
  usedSectors.forEach((s, idx) => {
    const z = (idx - sectorCenter) * sectorSpacing;
    const spr = makeSprite(s, "#eab308");
    spr.position.set(-xMax - 2.2, 0.4, z);
    spr.scale.set(3.5, 0.7, 1);
    group.add(spr);
  });

  // Trade spheres
  items.forEach((t, i) => {
    const tsMs = new Date(t.timestamp).getTime();
    const x = ((tsMs - t0) / tRange) * 24 - 12;
    const y = Math.max(-8, Math.min(8, (t.pnl / maxAbsPnl) * 8));
    const sIdx = usedSectors.indexOf(t.sector);
    const z = (sIdx - sectorCenter) * sectorSpacing;
    const size = Math.max(0.15, Math.min(0.9, Math.log10(Math.abs(t.pnl) + 10) * 0.18));
    const mine = highlightMine && t.trader === myTrader;
    const color = t.pnl >= 0 ? 0x22c55e : 0xef4444;
    const emissive = mine ? 0x8a6d0b : t.pnl >= 0 ? 0x052e16 : 0x450a0a;
    const dim = highlightMine && !mine;
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(mine ? size * 1.25 : size, 20, 20),
      new THREE.MeshStandardMaterial({ color, emissive, emissiveIntensity: mine ? 0.7 : 0.5, roughness: 0.3, metalness: 0.3,
        transparent: dim, opacity: dim ? 0.35 : 1 })
    );
    mesh.position.set(x, y, z);
    mesh.userData = { type: "trade", trader: t.trader, symbol: t.symbol, pnl: t.pnl, sector: t.sector, ts: tsMs };
    group.add(mesh);
  });

  const zSpan = Math.max(6, usedSectors.length * sectorSpacing + 2);
  const cursor = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 17, zSpan),
    new THREE.MeshBasicMaterial({ color: 0xeab308, transparent: true, opacity: 0, depthWrite: false })
  );
  cursor.userData = { type: "cursor" };
  cursor.name = "timeCursor";
  group.add(cursor);
  group.userData = { t0, tRange };

  return group;
}

// Scale cap so one extreme value can't flatten everything else
function robustMax(values) {
  const abs = values.map(v => Math.abs(v || 0)).sort((a, b) => a - b);
  if (!abs.length) return 1;
  const max = abs[abs.length - 1];
  const median = abs[Math.floor(abs.length / 2)];
  return Math.max(1, median > 0 ? Math.min(max, median * 3) : max);
}

function scaledHeight(v, cap, maxH) {
  const clamped = Math.max(-cap, Math.min(cap, v || 0));
  return { h: (clamped / cap) * maxH, capped: Math.abs(v || 0) > cap };
}

function addCapMarker(group, x, height, z) {
  const marker = new THREE.Mesh(
    new THREE.BoxGeometry(1.5, 0.12, 1.5),
    new THREE.MeshStandardMaterial({ color: 0xeab308, emissive: 0xeab308, emissiveIntensity: 0.6 })
  );
  marker.position.set(x, height, z);
  group.add(marker);
}

function buildTowers(traders, myTrader) {
  const group = new THREE.Group();
  if (!traders || traders.length === 0) return group;
  const maxAbs = robustMax(traders.map(t => t.total_pnl));
  const spacing = 3.6;
  const center = (traders.length - 1) / 2;

  traders.forEach((t, i) => {
    const { h: height, capped } = scaledHeight(t.total_pnl, maxAbs, 9);
    const color = t.total_pnl >= 0 ? 0x22c55e : 0xef4444;
    const mine = t.trader === myTrader;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, Math.abs(height) || 0.01, 1.2),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.15, roughness: 0.35, metalness: 0.25 })
    );
    if (mine) {
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry), new THREE.LineBasicMaterial({ color: 0xeab308 }));
      edges.scale.setScalar(1.06);
      mesh.add(edges);
    }
    const x = (i - center) * spacing;
    mesh.position.set(x, height / 2, 0);
    mesh.userData = { type: "trader", trader: t.trader, pnl: t.total_pnl, trades: t.trades, winRate: t.win_rate };
    group.add(mesh);
    if (capped) addCapMarker(group, x, height, 0);

    const nameSpr = makeSprite(t.trader, "#eab308");
    nameSpr.position.set(x, height + 0.7 * Math.sign(height || 1), 0);
    nameSpr.scale.set(3.6, 0.75, 1);
    group.add(nameSpr);

    const pnlSpr = makeSprite(`${niceMoney(t.total_pnl)}${capped ? " ▲" : ""}`, t.total_pnl >= 0 ? "#22c55e" : "#ef4444");
    pnlSpr.position.set(x, height + 1.35 * Math.sign(height || 1), 0);
    pnlSpr.scale.set(3.3, 0.7, 1);
    group.add(pnlSpr);
  });

  const title = makeSprite("Trader P&L Comparison", "#888");
  title.position.set(0, -1.4, 0);
  title.scale.set(5, 0.7, 1);
  group.add(title);

  return group;
}

function buildSectors(sectors) {
  const group = new THREE.Group();
  if (!sectors || sectors.length === 0) return group;
  const n = sectors.length;
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const spacing = 3.8;
  const centerX = (cols - 1) / 2;
  const centerZ = (rows - 1) / 2;
  const maxAbs = robustMax(sectors.map(s => s.total_pnl));

  sectors.forEach((s, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const x = (c - centerX) * spacing;
    const z = (r - centerZ) * spacing;
    const { h: height, capped } = scaledHeight(s.total_pnl, maxAbs, 7);
    const color = s.total_pnl >= 0 ? 0x22c55e : 0xef4444;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, Math.abs(height) || 0.01, 1.8),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.15, roughness: 0.4, metalness: 0.2 })
    );
    mesh.position.set(x, height / 2, z);
    mesh.userData = { type: "sector", sector: s.sector, pnl: s.total_pnl, trades: s.trades };
    group.add(mesh);
    if (capped) addCapMarker(group, x, height, z);

    const nameSpr = makeSprite(s.sector, "#eab308");
    nameSpr.position.set(x, height + 0.7 * Math.sign(height || 1), z);
    nameSpr.scale.set(3.6, 0.75, 1);
    group.add(nameSpr);

    const pnlSpr = makeSprite(`${niceMoney(s.total_pnl)}${capped ? " ▲" : ""}`, s.total_pnl >= 0 ? "#22c55e" : "#ef4444");
    pnlSpr.position.set(x, height + 1.35 * Math.sign(height || 1), z);
    pnlSpr.scale.set(3.3, 0.7, 1);
    group.add(pnlSpr);
  });

  return group;
}

function buildScene(view, data) {
  if (!data) return new THREE.Group();
  if (view === "scatter") return buildScatter(data.trades || [], data.my_trader, data.scope === "all");
  if (view === "towers") return buildTowers(data.traders || [], data.my_trader);
  if (view === "sectors") return buildSectors(data.sectors || []);
  return new THREE.Group();
}

// Renders the requested imperative scene inside the Canvas and provides
// hover state (via raycasting) for a tooltip overlay.
function SceneMount({ view, data, setHover, cutoff }) {
  const { scene, camera, gl } = useThree();
  const groupRef = useRef(null);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pointer = useRef({ x: 0, y: 0 });

  // Rebuild content on view/data change
  useEffect(() => {
    if (groupRef.current) {
      scene.remove(groupRef.current);
      // Dispose geometry/material to prevent leaks
      groupRef.current.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach(m => {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        }
      });
    }
    const g = buildScene(view, data);
    scene.add(g);
    groupRef.current = g;

    return () => {
      if (groupRef.current) scene.remove(groupRef.current);
    };
  }, [view, data, scene]);

  // Time slider: show only trades up to cutoff and move the cursor
  useEffect(() => {
    const g = groupRef.current;
    if (!g || view !== "scatter" || !g.userData.tRange) return;
    const active = cutoff != null;
    g.traverse((o) => {
      if (o.userData?.type === "trade") o.visible = !active || o.userData.ts <= cutoff;
      if (o.name === "timeCursor") {
        o.material.opacity = active ? 0.18 : 0;
        if (active) o.position.x = ((cutoff - g.userData.t0) / g.userData.tRange) * 24 - 12;
      }
    });
  }, [cutoff, view, data]);

  // Ambient/directional/point lights (imperative)
  useEffect(() => {
    const amb = new THREE.AmbientLight(0xffffff, 0.35);
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(10, 20, 10);
    const pnt = new THREE.PointLight(0xeab308, 0.4);
    pnt.position.set(-15, 10, -10);
    scene.add(amb); scene.add(dir); scene.add(pnt);
    scene.background = new THREE.Color(0x000000);

    // A subtle grid + starfield-like backdrop
    const grid = new THREE.GridHelper(40, 40, 0xeab308, 0x333333);
    grid.material.opacity = 0.4;
    grid.material.transparent = true;
    scene.add(grid);

    return () => { scene.remove(amb); scene.remove(dir); scene.remove(pnt); scene.remove(grid); };
  }, [scene]);

  // Pointer tracking for raycast hover
  useEffect(() => {
    const canvas = gl.domElement;
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };
    canvas.addEventListener("pointermove", onMove);
    return () => canvas.removeEventListener("pointermove", onMove);
  }, [gl]);

  // Frame loop for hover raycast
  useEffect(() => {
    let raf;
    const tick = () => {
      if (groupRef.current) {
        raycaster.setFromCamera(pointer.current, camera);
        const meshes = [];
        groupRef.current.traverse((o) => {
          if (o.isMesh && o.visible && o.userData?.type && o.userData.type !== "cursor") meshes.push(o);
        });
        const hits = raycaster.intersectObjects(meshes, false);
        setHover(hits[0]?.object?.userData || null);
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [camera, raycaster, setHover]);

  return null;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function VisualizationPage() {
  const [scope, setScope] = useState("me");
  const { data, loading, lastLoaded, reload } = useVizData(scope);
  const [view, setView] = useState("scatter");
  const views = scope === "me" ? VIEWS.filter(v => v.id !== "towers") : VIEWS;
  const switchScope = (next) => { setScope(next); if (next === "me" && view === "towers") setView("scatter"); };
  const [autoRotate, setAutoRotate] = useState(true);
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const [isFull, setIsFull] = useState(false);
  const nativeFs = typeof document !== "undefined" && !!document.documentElement.requestFullscreen;

  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === wrapRef.current);
    const onKey = (e) => { if (e.key === "Escape" && !nativeFs) setIsFull(false); };
    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("keydown", onKey);
    };
  }, [nativeFs]);

  const toggleFull = () => {
    if (!nativeFs) return setIsFull(f => !f);
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapRef.current?.requestFullscreen().catch(() => setIsFull(true));
  };

  const timeline = useTradeTimeline(data?.trades);
  const cutoff = view === "scatter" ? timeline.cutoff : null;

  const totals = useMemo(() => {
    if (!data) return null;
    if (cutoff == null && data.traders?.length) {
      const tr = data.traders;
      const trades = tr.reduce((a, x) => a + x.trades, 0);
      const wins = tr.reduce((a, x) => a + x.wins, 0);
      return {
        trades, wins, losses: tr.reduce((a, x) => a + (x.losses || 0), 0),
        winRate: trades ? Math.round((wins / trades) * 100) : 0,
        netPnl: tr.reduce((a, x) => a + x.total_pnl, 0),
      };
    }
    const t = (data.trades || []).filter(x => cutoff == null || new Date(x.timestamp).getTime() <= cutoff);
    const wins = t.filter(x => x.pnl > 0).length;
    const losses = t.filter(x => x.pnl < 0).length;
    const netPnl = t.reduce((s, x) => s + (x.pnl || 0), 0);
    return {
      trades: t.length,
      wins,
      losses,
      winRate: t.length ? Math.round((wins / t.length) * 100) : 0,
      netPnl,
    };
  }, [data, cutoff]);

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6" data-testid="visualization-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Box className="w-6 h-6 text-bt-gold" strokeWidth={1.5} />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">
              Visualization
            </h1>
          </div>
          <p className="text-base text-bt-text-secondary font-body mt-2">
            {scope === "me"
              ? "Every trade your Woodrow has closed, in 3D. Each ball is one trade."
              : "Every Woodrow trader on the platform. Your trades and tower are highlighted in gold."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ScopeSwitch scope={scope} setScope={switchScope} />
          <button
            onClick={() => setAutoRotate(a => !a)}
            className={`flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono transition-colors ${
              autoRotate
                ? "bg-bt-gold/20 border-bt-gold text-bt-gold"
                : "bg-bt-surface border-bt-border-subtle text-bt-text-secondary hover:text-bt-text-primary"
            }`}
            data-testid="toggle-auto-rotate"
          >
            <RotateCw className={`w-3 h-3 ${autoRotate ? "animate-spin" : ""}`} style={{ animationDuration: "4s" }} />
            Auto-rotate
          </button>
          <button
            onClick={reload}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-bt-border-subtle bg-bt-surface text-xs font-mono text-bt-text-secondary hover:text-bt-text-primary"
            data-testid="viz-reload"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
          </button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="viz-totals">
          <StatTile label="Trades" value={totals.trades.toLocaleString()} />
          <StatTile label="Wins" value={totals.wins.toLocaleString()} accent="text-bt-positive" />
          <StatTile label="Losses" value={totals.losses.toLocaleString()} accent="text-bt-negative" />
          <StatTile label="Win Rate" value={`${totals.winRate}%`} />
          <StatTile label="Net P&L" value={niceMoney(totals.netPnl)} accent={totals.netPnl > 0 ? "text-bt-positive" : totals.netPnl < 0 ? "text-bt-negative" : "text-bt-text-primary"} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="viz-view-selector">
        {views.map(v => {
          const Icon = v.icon;
          const active = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
                active
                  ? "bg-bt-gold/15 border-bt-gold text-bt-gold"
                  : "bg-bt-surface border-bt-border-subtle text-bt-text-secondary hover:text-bt-text-primary"
              }`}
              data-testid={`viz-view-${v.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-left">
                <span className="block text-sm font-medium">{v.label}</span>
                <span className="block text-[10px] text-bt-text-tertiary">{v.subtitle}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div
        ref={wrapRef}
        className={`bg-black border border-bt-border-subtle ${isFull ? "fixed inset-0 z-[100] w-screen h-screen border-0" : "relative h-[560px] sm:h-[640px]"}`}
        data-testid="viz-canvas-wrap"
        onPointerDown={(e) => { if (e.target.tagName === "CANVAS") setAutoRotate(false); }}
      >
        {isFull && (
          <FullscreenToolbar views={views} view={view} setView={setView} autoRotate={autoRotate} setAutoRotate={setAutoRotate} />
        )}
        <button
          onClick={toggleFull}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 border border-white/20 text-white/80 hover:text-white hover:border-white/50 text-xs font-mono transition-colors"
          title={isFull ? "Exit full screen (Esc)" : "Full screen"}
          data-testid="viz-fullscreen-btn"
        >
          {isFull ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{isFull ? "Exit" : "Full screen"}</span>
        </button>
        {loading && !data ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-bt-gold animate-spin" />
          </div>
        ) : (
          <>
            <Canvas camera={{ position: [18, 14, 22], fov: 45 }}>
              <Suspense fallback={null}>
                <SceneMount view={view} data={data} setHover={setHover} cutoff={cutoff} />
              </Suspense>
              <OrbitControls
                enableDamping={autoRotate}
                enablePan
                enableZoom
                enableRotate
                autoRotate={autoRotate}
                autoRotateSpeed={0.6}
                minDistance={8}
                maxDistance={70}
              />
            </Canvas>
            <HoverTooltip hover={hover} />
            <VizLegend view={view} scope={scope} offset={isFull} />
            {data && !loading && (data.trades || []).length === 0 && scope === "me" && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="viz-empty">
                <div className="pointer-events-auto text-center bg-black/80 border border-white/15 px-6 py-5">
                  <p className="text-sm text-white/85 mb-3">Your Woodrow hasn't closed any trades yet.</p>
                  <button onClick={() => switchScope("all")} className="px-3 py-1.5 text-xs font-mono bg-bt-gold text-bt-bg" data-testid="viz-empty-show-all">Show all traders</button>
                </div>
              </div>
            )}
            {view === "scatter" && timeline.ready && <TimeSlider timeline={timeline} />}
          </>
        )}
      </div>

      <p className="mt-3 text-xs text-bt-text-tertiary font-mono">
        Drag to rotate · scroll to zoom · right-click to pan · hover any object for details
        {lastLoaded && <span className="ml-3">Last loaded: {lastLoaded.toLocaleTimeString()}</span>}
      </p>
    </div>
  );
}

function ScopeSwitch({ scope, setScope }) {
  const btn = (id, label) => (
    <button onClick={() => setScope(id)} data-testid={`viz-scope-${id}`}
      className={`px-3 py-1.5 text-xs font-mono transition-colors ${scope === id ? "bg-bt-gold text-bt-bg" : "text-bt-text-secondary hover:text-bt-text-primary"}`}>
      {label}
    </button>
  );
  return (
    <div className="flex border border-bt-border-subtle bg-bt-surface" data-testid="viz-scope-switch">
      {btn("me", "My Woodrow")}{btn("all", "Show all traders")}
    </div>
  );
}

const LEGENDS = {
  scatter: {
    title: "Trade Scatter",
    explain: "Each ball is one closed trade. Watch how the wins and losses build up over time and across sectors.",
    keys: [["dot-green", "Winning trade"], ["dot-red", "Losing trade"], ["axis", "Left → right = time"], ["axis", "Height = profit or loss"], ["axis", "Rows = sectors"], ["axis", "Bigger ball = bigger $"]],
  },
  towers: {
    title: "Trader Towers",
    explain: "One tower per trader. Taller means more total profit; towers below the floor lost money.",
    keys: [["dot-green", "Net profit"], ["dot-red", "Net loss"], ["dot-gold", "You (gold outline)"], ["axis", "▲ = capped (real value on label)"]],
  },
  sectors: {
    title: "Sector Cubes",
    explain: "One cube per sector, showing where the money was made or lost.",
    keys: [["dot-green", "Profitable sector"], ["dot-red", "Losing sector"], ["axis", "Height = total P&L"], ["axis", "▲ = capped (real value on label)"]],
  },
};

function VizLegend({ view, scope, offset }) {
  const [open, setOpen] = useState(true);
  const l = LEGENDS[view];
  const keys = view === "scatter" && scope === "all" ? [...l.keys, ["dot-gold", "Your trades (gold glow)"]] : l.keys;
  const dot = { "dot-green": "bg-[#22c55e]", "dot-red": "bg-[#ef4444]", "dot-gold": "bg-[#eab308] shadow-[0_0_6px_#eab308]" };
  return (
    <div className={`absolute ${offset ? "top-16" : "top-3"} left-3 z-10 max-w-[300px]`}>
    <div className="max-w-[300px] bg-black/75 border border-white/15 backdrop-blur-md text-white" data-testid="viz-legend">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-white/70 hover:text-white" data-testid="viz-legend-toggle">
        How to read: {l.title} <span className="ml-2">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          <p className="text-xs text-white/80 leading-relaxed mb-2" data-testid="viz-legend-explain">{l.explain}</p>
          <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
            {keys.map(([k, txt]) => (
              <li key={txt} className="flex items-center gap-1.5 text-[11px] text-white/75">
                {dot[k] ? <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot[k]}`} /> : <span className="w-2.5 shrink-0 text-white/40">·</span>}
                {txt}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
    </div>
  );
}

function StatTile({ label, value, accent = "text-bt-text-primary" }) {
  return (
    <div className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">{label}</p>
      <p className={`text-lg font-mono font-medium ${accent}`}>{value}</p>
    </div>
  );
}

function FullscreenToolbar({ views, view, setView, autoRotate, setAutoRotate }) {
  return (
    <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5" data-testid="viz-fullscreen-toolbar">
      {views.map(v => {
        const Icon = v.icon;
        return (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-mono transition-colors ${
              view === v.id ? "bg-white/15 border-white/60 text-white" : "bg-black/70 border-white/20 text-white/70 hover:text-white"
            }`}
            data-testid={`viz-fs-view-${v.id}`}
          >
            <Icon className="w-3.5 h-3.5" />{v.label}
          </button>
        );
      })}
      <button
        onClick={() => setAutoRotate(a => !a)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-mono transition-colors ${
          autoRotate ? "bg-white/15 border-white/60 text-white" : "bg-black/70 border-white/20 text-white/70 hover:text-white"
        }`}
        data-testid="viz-fs-auto-rotate"
      >
        <RotateCw className="w-3.5 h-3.5" />Auto-rotate
      </button>
    </div>
  );
}

function HoverTooltip({ hover }) {
  if (!hover || !hover.type) return null;
  const isGain = (hover.pnl ?? 0) >= 0;
  const color = isGain ? "#22c55e" : "#ef4444";
  return (
    <div
      className="absolute top-14 right-3 z-10 bg-black/85 border border-bt-gold px-3 py-2 text-xs font-mono pointer-events-none"
      data-testid="viz-hover-tooltip"
    >
      {hover.type === "trade" && (
        <>
          <div className="text-bt-gold">{hover.symbol}</div>
          <div className="text-bt-text-secondary">{hover.trader} · {hover.sector}</div>
          <div style={{ color }}>{niceMoney(hover.pnl)}</div>
        </>
      )}
      {hover.type === "trader" && (
        <>
          <div className="text-bt-gold">{hover.trader}</div>
          <div style={{ color }}>{niceMoney(hover.pnl)}</div>
          <div className="text-bt-text-secondary">{hover.trades} trades · {hover.winRate}% win</div>
        </>
      )}
      {hover.type === "sector" && (
        <>
          <div className="text-bt-gold">{hover.sector}</div>
          <div style={{ color }}>{niceMoney(hover.pnl)}</div>
          <div className="text-bt-text-secondary">{hover.trades} trades</div>
        </>
      )}
    </div>
  );
}
